import React, { useState, useEffect, useCallback } from 'react';
import { getUsers, getInventories, createUser, updateUser, toggleUserActive, deleteUser, exportUsers, adminResetPassword, resendInvitation } from '../../lib/api';
import { downloadBlob } from '../../lib/downloadHelper';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { toast } from 'sonner';
import {
  Users as UsersIcon,
  Edit,
  Trash2,
  Loader2,
  AlertCircle,
  Download,
  Printer,
  Plus,
  UserPlus,
  Shield,
  ShieldCheck,
  Wrench,
  KeyRound,
  Mail,
  MailCheck
} from 'lucide-react';

const ROLE_LABELS = {
  admin: 'Admin',
  manager: 'Manager',
  technician: 'Service Professional'
};

const ROLE_ICONS = {
  admin: ShieldCheck,
  manager: Shield,
  technician: Wrench
};

const ROLE_STYLES = {
  admin: 'bg-red-100 text-red-800',
  manager: 'bg-[#E6F2FA] text-[#0776BD]',
  technician: 'bg-amber-100 text-amber-800'
};

const DEFAULT_TEMP_PASSWORD = process.env.REACT_APP_DEFAULT_TEMP_PASSWORD || 'changeme123';

const EMPTY_FORM = {
  name: '',
  email: '',
  password: DEFAULT_TEMP_PASSWORD,
  phone_number: '',
  role: 'technician',
  assigned_inventory_id: '',
  active: true
};

// Phone number formatter: (xxx) xxx-xxxx
const formatPhone = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const getPhoneDigits = (value) => (value || '').replace(/\D/g, '');

const Users = () => {
  const [users, setUsers] = useState([]);
  const [inventories, setInventories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportingUsers, setExportingUsers] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [userToReset, setUserToReset] = useState(null);
  const [resetPassword, setResetPassword] = useState(DEFAULT_TEMP_PASSWORD);
  const [resetting, setResetting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, invRes] = await Promise.all([getUsers(), getInventories()]);
      setUsers(usersRes.data);
      setInventories(invRes.data);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreateDialog = () => {
    setIsCreating(true);
    setEditingUser(null);
    setFormData({ ...EMPTY_FORM });
    setError('');
    setDialogOpen(true);
  };

  const openEditDialog = (user) => {
    setIsCreating(false);
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      phone_number: user.phone_number || '',
      role: user.role,
      assigned_inventory_id: user.assigned_inventory_id || '',
      active: user.active
    });
    setError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) { setError('Name is required'); return; }
    if (!formData.email.trim()) { setError('Email is required'); return; }
    if (!formData.role) { setError('Role is required'); return; }
    const phoneDigits = getPhoneDigits(formData.phone_number);
    if (phoneDigits.length > 0 && phoneDigits.length !== 10) {
      setError('Please enter a valid 10-digit phone number'); return;
    }
    if (isCreating && !formData.password.trim()) { setError('Password is required'); return; }

    setSaving(true);
    setError('');

    try {
      const formattedPhone = phoneDigits.length === 10 ? formatPhone(phoneDigits) : '';
      
      if (isCreating) {
        const payload = {
          name: formData.name.trim(),
          email: formData.email.trim(),
          password: formData.password,
          role: formData.role,
          phone_number: formattedPhone,
          assigned_inventory_id: formData.assigned_inventory_id || null,
          active: formData.active,
          send_invitation: true
        };
        const res = await createUser(payload);
        const invSent = res.data?.invitation_sent;
        toast.success(invSent
          ? 'User created and invitation email sent!'
          : 'User created (invitation email could not be sent)');
      } else {
        const payload = {
          name: formData.name.trim(),
          email: formData.email.trim(),
          role: formData.role,
          phone_number: formattedPhone,
          assigned_inventory_id: formData.role === 'technician' ? (formData.assigned_inventory_id || null) : null,
          active: formData.active
        };
        await updateUser(editingUser.id, payload);
        toast.success('User updated successfully');
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to ${isCreating ? 'create' : 'update'} user`);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (targetUser) => {
    setTogglingId(targetUser.id);
    try {
      await toggleUserActive(targetUser.id);
      toast.success(`User ${targetUser.active ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to toggle status');
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    setDeleting(true);
    try {
      await deleteUser(userToDelete.id);
      toast.success('User deleted');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const handleResetPassword = async () => {
    if (!userToReset) return;
    setResetting(true);
    try {
      await adminResetPassword(userToReset.id, { new_password: resetPassword });
      toast.success(`Password reset for ${userToReset.name}. They will be prompted to set a new password on next login.`);
      setResetDialogOpen(false);
      setUserToReset(null);
      setResetPassword(DEFAULT_TEMP_PASSWORD);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const handleResendInvitation = async (targetUser) => {
    try {
      await resendInvitation(targetUser.id);
      toast.success(`Invitation email resent to ${targetUser.email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend invitation');
    }
  };

  const getInventoryName = (id) => {
    if (!id) return 'Not assigned';
    const inv = inventories.find(i => i.id === id);
    return inv ? inv.inventory_name : 'Unknown';
  };

  const vehicleInventories = inventories.filter(i => i.type === 'vehicle');

  return (
    <div className="space-y-6 animate-slide-in" data-testid="users-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="heading-2 text-[#1F2937]">User Management</h1>
          <p className="text-[#6B7280]">Manage team members, roles, and access</p>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button
            onClick={openCreateDialog}
            className="gap-2 bg-[#0776BD] hover:bg-[#055A92] text-white rounded-lg"
            data-testid="create-user-btn"
          >
            <UserPlus size={18} /> Create User
          </Button>
          <Button onClick={async () => {
            setExportingUsers(true);
            try {
              const res = await exportUsers();
              downloadBlob(res.data, `users_${new Date().toISOString().split('T')[0]}.xlsx`);
            } catch (e) { void 0; toast.error('Export failed.'); } finally { setExportingUsers(false); }
          }} disabled={exportingUsers || users.length === 0}
            className="gap-2 bg-green-600 hover:bg-green-700 text-white rounded-lg" data-testid="export-users-btn">
            {exportingUsers ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download size={18} />}
            Export
          </Button>
          <Button onClick={() => window.print()} variant="outline"
            className="gap-2 rounded-lg border-[#0776BD] text-[#0776BD]" data-testid="print-users-btn">
            <Printer size={18} /> Print
          </Button>
        </div>
      </div>

      <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-[#1F2937] flex items-center gap-2">
            <UsersIcon size={20} className="text-[#0776BD]" />
            Team Members ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#0776BD]" />
            </div>
          ) : users.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-[#6B7280]">
              <UsersIcon size={40} className="mb-3 opacity-40" />
              <p className="text-sm">No users found. Create one to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F5F6F7]">
                    <TableHead className="font-semibold text-[#1F2937]">Name</TableHead>
                    <TableHead className="font-semibold text-[#1F2937]">Email</TableHead>
                    <TableHead className="font-semibold text-[#1F2937]">Phone</TableHead>
                    <TableHead className="font-semibold text-[#1F2937]">Role</TableHead>
                    <TableHead className="font-semibold text-[#1F2937]">Assigned Inventory</TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-center">Status</TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-right print:hidden">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => {
                    const RoleIcon = ROLE_ICONS[u.role] || Shield;
                    return (
                      <TableRow key={u.id} className="hover:bg-[#F5F6F7]" data-testid={`user-row-${u.id}`}>
                        <TableCell className="font-medium text-[#1F2937]">{u.name}</TableCell>
                        <TableCell className="text-[#6B7280]">{u.email}</TableCell>
                        <TableCell className="text-[#6B7280]">{u.phone_number || <span className="text-gray-300">--</span>}</TableCell>
                        <TableCell>
                          <Badge className={`${ROLE_STYLES[u.role] || 'bg-gray-100 text-gray-600'} border-0 gap-1`} data-testid={`user-role-badge-${u.id}`}>
                            <RoleIcon size={12} />
                            {ROLE_LABELS[u.role] || u.role}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-[#6B7280]">
                          {u.role === 'technician' ? getInventoryName(u.assigned_inventory_id) : <span className="text-gray-400">--</span>}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-2">
                            {!u.password_initialized ? (
                              <Badge className="bg-amber-100 text-amber-700 border-0 gap-1" data-testid={`user-status-badge-${u.id}`}>
                                <Mail size={12} /> Pending Setup
                              </Badge>
                            ) : (
                              <Badge
                                className={u.active
                                  ? 'bg-green-100 text-green-800 border-0'
                                  : 'bg-red-100 text-red-700 border-0'}
                                data-testid={`user-status-badge-${u.id}`}
                              >
                                {u.active ? 'Active' : 'Inactive'}
                              </Badge>
                            )}
                            <Switch
                              checked={u.active}
                              disabled={togglingId === u.id}
                              onCheckedChange={() => handleToggleActive(u)}
                              className="print:hidden"
                              data-testid={`user-toggle-active-${u.id}`}
                            />
                          </div>
                        </TableCell>
                        <TableCell className="text-right print:hidden">
                          <div className="flex justify-end gap-1">
                            {!u.password_initialized && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleResendInvitation(u)}
                                className="hover:bg-green-50 text-green-600"
                                title="Resend Invitation Email"
                                data-testid={`resend-invite-${u.id}`}
                              >
                                <Mail size={16} />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => { setUserToReset(u); setResetPassword(DEFAULT_TEMP_PASSWORD); setResetDialogOpen(true); }}
                              className="hover:bg-amber-50 text-amber-600"
                              title="Reset Password"
                              data-testid={`reset-password-${u.id}`}
                            >
                              <KeyRound size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditDialog(u)}
                              className="hover:bg-[#E6F2FA] text-[#0776BD]"
                              data-testid={`edit-user-${u.id}`}
                            >
                              <Edit size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => { setUserToDelete(u); setDeleteDialogOpen(true); }}
                              className="hover:bg-red-50 text-red-500"
                              data-testid={`delete-user-${u.id}`}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#1F2937] flex items-center gap-2">
              {isCreating ? <><UserPlus size={20} className="text-[#0776BD]" /> Create User</> : <><Edit size={20} className="text-[#0776BD]" /> Edit User</>}
            </DialogTitle>
          </DialogHeader>

          {error && (
            <Alert variant="destructive" className="rounded-lg">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#1F2937]">Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Full name"
                className="h-11 rounded-lg border-gray-300 focus:border-[#0776BD]"
                data-testid="user-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#1F2937]">Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="user@mrelectric.com"
                className="h-11 rounded-lg border-gray-300 focus:border-[#0776BD]"
                data-testid="user-email-input"
              />
            </div>

            {isCreating && (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#1F2937]">Password *</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Temporary password"
                  className="h-11 rounded-lg border-gray-300 focus:border-[#0776BD]"
                  data-testid="user-password-input"
                />
                <p className="text-xs text-[#6B7280]">Default: {DEFAULT_TEMP_PASSWORD}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#1F2937]">Phone Number</Label>
              <Input
                type="tel"
                value={formData.phone_number}
                onChange={(e) => setFormData({ ...formData, phone_number: formatPhone(e.target.value) })}
                placeholder="(xxx) xxx-xxxx"
                className="h-11 rounded-lg border-gray-300 focus:border-[#0776BD]"
                data-testid="user-phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#1F2937]">Role *</Label>
              <Select value={formData.role} onValueChange={(val) => setFormData({ ...formData, role: val, assigned_inventory_id: val !== 'technician' ? '' : formData.assigned_inventory_id })}>
                <SelectTrigger className="h-11 rounded-lg border-gray-300" data-testid="user-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="technician">Service Professional</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.role === 'technician' && (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#1F2937]">Assigned Inventory (Van)</Label>
                <Select
                  value={formData.assigned_inventory_id || 'none'}
                  onValueChange={(val) => setFormData({ ...formData, assigned_inventory_id: val === 'none' ? '' : val })}
                >
                  <SelectTrigger className="h-11 rounded-lg border-gray-300" data-testid="user-inventory-select">
                    <SelectValue placeholder="Select inventory" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Inventory Assigned</SelectItem>
                    {(vehicleInventories.length > 0 ? vehicleInventories : inventories).map(inv => (
                      <SelectItem key={inv.id} value={inv.id}>{inv.inventory_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-amber-600">Without an inventory, the Service Professional cannot use or transfer items</p>
              </div>
            )}

            {!isCreating && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                <div>
                  <Label className="text-sm font-medium text-[#1F2937]">Active Status</Label>
                  <p className="text-xs text-[#6B7280]">Inactive users cannot log in</p>
                </div>
                <Switch
                  checked={formData.active}
                  onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                  data-testid="user-active-switch"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-lg" data-testid="cancel-user-btn">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#0776BD] hover:bg-[#055A92] rounded-lg" data-testid="save-user-btn">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {isCreating ? 'Create User' : 'Update User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#1F2937]">Delete User</DialogTitle>
          </DialogHeader>
          <p className="text-[#6B7280]">Are you sure you want to delete <strong>"{userToDelete?.name}"</strong>? This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="rounded-lg" data-testid="cancel-delete-user-btn">Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting} className="rounded-lg" data-testid="confirm-delete-user-btn">
              {deleting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent className="max-w-md rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#1F2937] flex items-center gap-2">
              <KeyRound size={20} className="text-amber-600" /> Reset Password
            </DialogTitle>
          </DialogHeader>
          <p className="text-[#6B7280]">
            Set a temporary password for <strong>{userToReset?.name}</strong>. They will be prompted to set a new password on their next login.
          </p>
          <div className="space-y-2">
            <Label className="text-sm font-medium text-[#1F2937]">Temporary Password</Label>
            <Input
              type="text"
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
              placeholder="Temporary password"
              className="h-11 rounded-lg border-gray-300 focus:border-[#0776BD]"
              data-testid="reset-password-input"
            />
            <p className="text-xs text-[#6B7280]">Min. 6 characters. Default: {DEFAULT_TEMP_PASSWORD}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialogOpen(false)} className="rounded-lg">Cancel</Button>
            <Button onClick={handleResetPassword} disabled={resetting || resetPassword.length < 6} className="bg-amber-600 hover:bg-amber-700 rounded-lg" data-testid="confirm-reset-password-btn">
              {resetting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
